import { useState, useEffect } from "react";
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    PieChart, Pie, Cell, Line, ComposedChart, Area
} from 'recharts';
import useAuth from "./hooks/useAuth";

const PronunciationTrendsDisplay = ({ language, username }) => {
    const [trendsData, setTrendsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scoreType, setScoreType] = useState('PronScore');
    const [trendAlgorithm, setTrendAlgorithm] = useState('global');
    const [sentenceLength, setSentenceLength] = useState('all'); // New state for sentence length filter
    const { auth, setAuth } = useAuth();

    const scoreTypes = {
        'PronScore': { name: 'Overall Pronunciation', color: '#8884d8' },
        'AccuracyScore': { name: 'Accuracy', color: '#82ca9d' },
        'FluencyScore': { name: 'Fluency', color: '#ffc658' },
        'CompletenessScore': { name: 'Completeness', color: '#ff8042' }
    };

    const sentenceLengthOptions = {
        'all': { name: 'All Lengths', description: 'Show all sentence lengths' },
        '1-5': { name: 'Short (1-5 words)', description: 'Very short sentences' },
        '5-10': { name: 'Medium (5-10 words)', description: 'Medium length sentences' },
        '10-15': { name: 'Long (10-15 words)', description: 'Longer sentences' },
        '15+': { name: 'Very Long (15+ words)', description: 'Complex sentences' }
    };

    // Fetch pronunciation trends data
    useEffect(() => {
        if (language) {
            fetchPronunciationTrends();
        }
    }, [language, username, scoreType, trendAlgorithm, sentenceLength]); // Add sentenceLength to dependencies

    const fetchPronunciationTrends = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            let query = `${process.env.REACT_APP_USER_API_URL}/trends/${language}/pronunciation_trends`;
            
            const params = new URLSearchParams();
            params.append('score_type', scoreType);
            params.append('trend_algorithm', trendAlgorithm);
            params.append('sentence_length', sentenceLength); // Add sentence length parameter
            if (username) params.append('username', username);
            
            query += `?${params.toString()}`;

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                setTrendsData(data);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching pronunciation trends:", error);
            setTrendsData(null);
        } finally {
            setLoading(false);
        }
    };

    // Add sentence length distribution chart
    const SentenceLengthDistribution = ({ distribution }) => {
        if (!distribution) return null;

        const data = Object.entries(distribution).map(([range, count]) => ({
            name: sentenceLengthOptions[range]?.name || range,
            value: count,
            range: range
        }));

        const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe'];

        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="font-semibold text-gray-800 mb-4">Sentence Length Distribution</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => [`${value} sentences`, 'Count']} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                        {data.map((item, index) => (
                            <div key={item.range} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <div 
                                        className="w-4 h-4 rounded-full" 
                                        style={{ backgroundColor: colors[index % colors.length] }}
                                    ></div>
                                    <span className="text-sm font-medium">{item.name}</span>
                                </div>
                                <span className="text-sm font-semibold text-gray-700">{item.value} sentences</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const ScoreDistributionChart = ({ distribution }) => {
        const data = [
            { name: 'Excellent (90-100)', value: distribution.excellent, color: '#10B981' },
            { name: 'Good (70-89)', value: distribution.good, color: '#F59E0B' },
            { name: 'Fair (50-69)', value: distribution.fair, color: '#EF4444' },
            { name: 'Needs Work (<50)', value: distribution.needs_work, color: '#DC2626' }
        ];

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} sentences`, 'Count']} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                    {data.map((item, index) => (
                        <div key={item.name} className="flex items-center gap-2">
                            <div 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: item.color }}
                            ></div>
                            <span className="text-sm font-medium">{item.name}</span>
                            <span className="text-sm text-gray-500">({item.value} sentences)</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const DailyAveragesChart = ({ data, scoreType }) => {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="font-semibold text-gray-800 mb-4">Daily Performance Averages</h4>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="date" 
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis yAxisId="left" domain={[0, 100]} tick={{ fontSize: 12 }} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Legend />
                            <Area 
                                yAxisId="left"
                                type="monotone" 
                                dataKey={scoreType} 
                                fill={scoreTypes[scoreType].color}
                                stroke={scoreTypes[scoreType].color}
                                fillOpacity={0.3}
                                name={scoreTypes[scoreType].name}
                            />
                            <Bar 
                                yAxisId="right"
                                dataKey="sentence_length" 
                                fill="#8884d8" 
                                name="Sentence Length"
                                opacity={0.6}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    const TrendAnalysis = ({ trendsData, scoreType, trendAlgorithm, onAlgorithmChange }) => {
        const algorithmDescriptions = {
            'global': { name: 'Global Linear', icon: '📈' },
            'ema': { name: 'Exponential Smoothing', icon: '🔄' },
            'loess': { name: 'LOESS Regression', icon: '🎯' },
            'segmented': { name: 'Segmented Linear', icon: '📊' },
            'adaptive': { name: 'Adaptive', icon: '🤖' }
        };

        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
                    <div>
                        <h4 className="font-semibold text-gray-800 text-lg">Performance Trends</h4>
                        <p className="text-sm text-gray-600">{trendsData.length} data points analyzed</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Score Type Selector */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {Object.entries(scoreTypes).map(([key, config]) => (
                                <button
                                    key={key}
                                    onClick={() => setScoreType(key)}
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        scoreType === key 
                                            ? 'bg-white text-gray-800 shadow-sm' 
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    {config.name}
                                </button>
                            ))}
                        </div>
                        
                        {/* Trend Algorithm Selector */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {Object.entries(algorithmDescriptions).map(([key, algo]) => (
                                <button
                                    key={key}
                                    onClick={() => onAlgorithmChange(key)}
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                                        trendAlgorithm === key 
                                            ? 'bg-white text-gray-800 shadow-sm' 
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    <span>{algo.icon}</span>
                                    <span className="hidden sm:inline">{algo.name}</span>
                                </button>
                            ))}
                        </div>

                        {/* Sentence Length Selector */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {Object.entries(sentenceLengthOptions).map(([key, option]) => (
                                <button
                                    key={key}
                                    onClick={() => setSentenceLength(key)}
                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        sentenceLength === key 
                                            ? 'bg-white text-gray-800 shadow-sm' 
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                    title={option.description}
                                >
                                    {option.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Trend Chart */}
                <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={trendsData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                dataKey="date" 
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Legend />
                            <Line 
                                type="monotone" 
                                dataKey={scoreType} 
                                stroke={scoreTypes[scoreType].color}
                                strokeWidth={2}
                                dot={{ fill: scoreTypes[scoreType].color, r: 2 }}
                                name={`${scoreTypes[scoreType].name} (Actual)`}
                            />
                            <Line 
                                type="monotone" 
                                dataKey={`${scoreType}_trend`} 
                                stroke="#000000"
                                strokeWidth={3}
                                strokeDasharray="5 5"
                                name={`${scoreTypes[scoreType].name} Trend`}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    const renderOverview = () => {
        if (!trendsData) return null;

        return (
            <div className="space-y-6">
                {/* Overview Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="text-2xl font-bold text-blue-700">
                            {trendsData.total_sentences}
                        </div>
                        <div className="text-sm text-blue-600 font-medium">Total Sentences</div>
                        <div className="text-xs text-blue-500 mt-1">
                            {sentenceLengthOptions[sentenceLength].name}
                        </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-700">
                            {trendsData.statistics?.average_score?.toFixed(1) || 0}
                        </div>
                        <div className="text-sm text-green-600 font-medium">Average Score</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <div className="text-2xl font-bold text-purple-700">
                            {trendsData.statistics?.median_score?.toFixed(1) || 0}
                        </div>
                        <div className="text-sm text-purple-600 font-medium">Median Score</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <div className="text-2xl font-bold text-orange-700">
                            {trendsData.statistics?.average_sentence_length?.toFixed(0) || 0}
                        </div>
                        <div className="text-sm text-orange-600 font-medium">Avg Words/Sentence</div>
                    </div>
                </div>

                {/* Sentence Length Distribution - Only show when viewing all lengths */}
                {sentenceLength === 'all' && trendsData.sentence_length_distribution && (
                    <SentenceLengthDistribution distribution={trendsData.sentence_length_distribution} />
                )}

                {/* Score Distribution */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h4 className="font-semibold text-gray-800 mb-4">Score Distribution</h4>
                    <ScoreDistributionChart distribution={trendsData.score_distribution} />
                </div>

                {/* Daily Averages */}
                {trendsData.daily_averages && trendsData.daily_averages.length > 0 && (
                    <DailyAveragesChart data={trendsData.daily_averages} scoreType={scoreType} />
                )}

                {/* Trends */}
                {trendsData.trends && trendsData.trends.length > 0 && (
                    <TrendAnalysis 
                        trendsData={trendsData.trends}
                        scoreType={scoreType}
                        trendAlgorithm={trendAlgorithm}
                        onAlgorithmChange={setTrendAlgorithm}
                    />
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <div className="text-md font-medium text-gray-700">Loading pronunciation trends...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">Pronunciation Trends</h3>
                    <div className="text-sm text-gray-600">
                        {language} • {username || 'My Data'} • {sentenceLengthOptions[sentenceLength].name}
                    </div>
                </div>
            </div>

            {/* Content */}
            {trendsData && renderOverview()}
            
            {!trendsData && !loading && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <div className="text-gray-400 text-4xl mb-2">🎯</div>
                    <h4 className="font-semibold text-gray-600 mb-2">No Pronunciation Data Available</h4>
                    <p className="text-gray-500">No pronunciation assessment results found for {language}</p>
                    <button
                        onClick={fetchPronunciationTrends}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Retry Load Data
                    </button>
                </div>
            )}
        </div>
    );
};

export default PronunciationTrendsDisplay;